import { FormInstance } from "../../../components/form-library/types";
import { LineItemValues } from "../types/LineItem";
import {
    DEFAULT_VAT_RATE_OPTIONS,
    DEFAULT_VAT_RATE_PERCENTAGE,
    DefaultVatRateOption,
} from "../types/VatCode";
import { Money, toMoneyFromCents } from "../../../lib/Money";
import type { CurrencyEnum } from '../../../generated/enums.ts';

export function calculateVatAmountsFromTotal(totalAmountCents: number, vatRatePercentage: number): {
    vatAmount: number;
    netAmount: number
} {
    if (!vatRatePercentage || vatRatePercentage === 0) {
        return { vatAmount: 0, netAmount: totalAmountCents };
    }
    const vatRate = vatRatePercentage / 10000;
    const netAmount = Math.round(totalAmountCents / (1 + vatRate));
    const vatAmount = totalAmountCents - netAmount;
    return {vatAmount, netAmount};
}

export function calculateVatAmountsFromNet(netAmountCents: number, vatRatePercentage: number): {
    vatAmount: number;
    totalAmount: number
} {
    if (!vatRatePercentage || vatRatePercentage === 0) {
        return { vatAmount: 0, totalAmount: netAmountCents };
    }
    const vatRate = vatRatePercentage / 10000;
    const vatAmount = Math.round(netAmountCents * vatRate);
    const totalAmount = netAmountCents + vatAmount;
    return {vatAmount, totalAmount};
}

export function lineItemFieldName(itemId: string, fieldName: string): string {
    return `line_items.${itemId}.${fieldName}`;
}

export function getLineItemValuesFromForm(lineItemIds: string[], form: FormInstance): LineItemValues[] {
    return lineItemIds.map((id) => {
        const vatAmountMoney = form.getFieldValue(lineItemFieldName(id, "vat_amount")) as Money | null;
        const netAmountMoney = form.getFieldValue(lineItemFieldName(id, "net_amount")) as Money | null;
        return {
            vatAmount: vatAmountMoney?.signed_amount ?? 0,
            netAmount: netAmountMoney?.signed_amount ?? 0,
        };
    });
}

export function hasLineItemBookingCategory(form: FormInstance, lineItemIds: string[]): boolean {
    return lineItemIds.some((id) => {
        const value = form.getFieldValue(lineItemFieldName(id, "category_id"));
        return value !== null && value !== undefined && value !== '';
    });
}

export function buildSalesDirectionResetValues(lineItemIds: string[]): Record<string, unknown> {
    const values: Record<string, unknown> = { is_vat_toggled_on: true };
    lineItemIds.forEach((id) => {
        values[lineItemFieldName(id, "category_id")] = null;
        values[lineItemFieldName(id, "subledger_id")] = null;
    });
    return values;
}

export function calculateLineItemTotals(lineItemValues: LineItemValues[]) {
    const { vatAmountsSum, netAmountsSum } = lineItemValues.reduce(
      (sum, item) => ({
        vatAmountsSum: sum.vatAmountsSum + item.vatAmount,
        netAmountsSum: sum.netAmountsSum + item.netAmount,
      }),
      { vatAmountsSum: 0, netAmountsSum: 0 }
    );
  
    return {
      vatAmountsSum,
      netAmountsSum,
    };
  }

export const ADJUST_TOTALS_BANNER_TOLERANCE_CENTS = 50;

export function getTotalsDifference(
  documentTotal: Money | null | undefined,
  documentVat: Money | null | undefined,
  calculatedTotalCents: number,
  calculatedVatCents: number
): { grossDiff: number; vatDiff: number; isWithinTolerance: boolean } {
  const documentTotalCents = documentTotal?.signed_amount ?? 0;
  const documentVatCents = documentVat?.signed_amount ?? 0;
  const grossDiffCents = documentTotalCents - calculatedTotalCents;
  const vatDiffCents = documentVatCents - calculatedVatCents;
  const isWithinTolerance = Math.abs(grossDiffCents) <= ADJUST_TOTALS_BANNER_TOLERANCE_CENTS;
  return {
    grossDiff: grossDiffCents,
    vatDiff: vatDiffCents,
    isWithinTolerance,
  };
}

export function getNetDiffFromGrossAndVat(grossDiffCents: number, vatDiffCents: number): number {
  return grossDiffCents - vatDiffCents;
}

export function computeAdjustTotalsForLastLine(
  grossDiffCents: number,
  vatDiffCents: number,
  lastLineItemNetCents: number,
  lastLineItemVatCents: number,
  calculatedNetSumCents: number,
  calculatedVatSumCents: number
): { adjustedNetCents: number; adjustedVatCents: number; newNetSumCents: number; newVatSumCents: number } {
  const netDiff = getNetDiffFromGrossAndVat(grossDiffCents, vatDiffCents);
  return {
    adjustedNetCents: lastLineItemNetCents + netDiff,
    adjustedVatCents: lastLineItemVatCents + vatDiffCents,
    newNetSumCents: calculatedNetSumCents + netDiff,
    newVatSumCents: calculatedVatSumCents + vatDiffCents,
  };
}

export function buildVatRateOptionsFromLineItems(
    lineItems: Array<{ vat_rate_percentage: number | null | undefined }>,
): DefaultVatRateOption[] {
    const vatRateOptions: DefaultVatRateOption[] = [...DEFAULT_VAT_RATE_OPTIONS];
    
    lineItems.forEach((item) => {
        const currentVatRatePercentage = item.vat_rate_percentage;
        if (currentVatRatePercentage != null && !vatRateOptions.some(opt => opt.percentage === currentVatRatePercentage)) {
            const percentageDisplay = currentVatRatePercentage / 100;
            vatRateOptions.push({
                value: `CUSTOM_${currentVatRatePercentage}`,
                label: `Custom (${percentageDisplay}%)`,
                percentage: currentVatRatePercentage
            });
        }
    });
    
    return vatRateOptions;
}

export type MergeLineItemValues = {
    netAmountsCents: number;
    totalAmountsCents: number;
    vatAmountsCents: number;
    vatRatePercentage: number;
    vatId: string | null;
    vatRatePercentageId: string | null;
};

function vatAmountErrorForRate(
    netCents: number,
    vatCents: number,
    totalCents: number,
    percentage: number,
): number {
    const fromNet = Math.abs(calculateVatAmountsFromNet(netCents, percentage).vatAmount - vatCents);
    const fromTotal = Math.abs(calculateVatAmountsFromTotal(totalCents, percentage).vatAmount - vatCents);
    return Math.min(fromNet, fromTotal);
}

function findBestMatchingVatRateOption(
    netCents: number,
    vatCents: number,
    totalCents: number,
    options: DefaultVatRateOption[],
): DefaultVatRateOption | null {
    if (options.length === 0) {
        return null;
    }

    return options.reduce((best, option) => {
        const bestError = vatAmountErrorForRate(netCents, vatCents, totalCents, best.percentage);
        const optionError = vatAmountErrorForRate(netCents, vatCents, totalCents, option.percentage);
        return optionError < bestError ? option : best;
    });
}

export function computeMergedLineItemValues(
    billTotalAmountsCents: number,
    billVatAmountsCents: number,
    defaultVatRateOptions: DefaultVatRateOption[],
): MergeLineItemValues {
    const netAmountsCents = billTotalAmountsCents - billVatAmountsCents;
    const matchedOption = findBestMatchingVatRateOption(
        netAmountsCents,
        billVatAmountsCents,
        billTotalAmountsCents,
        defaultVatRateOptions,
    );

    const vatRatePercentage = matchedOption?.percentage ?? DEFAULT_VAT_RATE_PERCENTAGE;
    const vatRatePercentageId = defaultVatRateOptions.find(
        (opt) => opt.percentage === vatRatePercentage,
    )?.value ?? null;

    const { netAmount, vatAmount } = calculateVatAmountsFromTotal(
        billTotalAmountsCents,
        vatRatePercentage,
    );

    return {
        netAmountsCents: netAmount,
        totalAmountsCents: billTotalAmountsCents,
        vatAmountsCents: vatAmount,
        vatRatePercentage,
        vatId: null,
        vatRatePercentageId,
    };
}

type BuildMergedLineItemData = {
    currency: CurrencyEnum;
    nameOrDescription: string;

    firstCategoryId: string | null;
    vatId: string | null;
    vatRatePercentageId: string | null;
    netAmountsCents: number;
    totalAmountsCents: number;
    vatAmountsCents: number;
    vatRatePercentage: number;
};

export function buildMergedLineItem(
    data: BuildMergedLineItemData
): Record<string, Money | number | string | null> {
    return {
        name_or_description: data.nameOrDescription,
        net_amount: toMoneyFromCents(data.netAmountsCents, data.currency),
        vat_amount: toMoneyFromCents(data.vatAmountsCents, data.currency),
        total_amount: toMoneyFromCents(data.totalAmountsCents, data.currency),
        vat_id: data.vatId,
        vat_rate_percentage_id: data.vatRatePercentageId,
        category_id: data.firstCategoryId || '',
        vat_rate_percentage: data.vatRatePercentage,
    };
}
